import { vec3 } from 'gl-matrix';
import { useEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
    environmentState,
    lowerSideViewState,
    mainViewState,
    upperSideViewState,
} from '~/state/atoms/engineAtoms';
import { reviewToolState } from '~/state/atoms/toolsAtoms';
import {
    currenPositionAutoScrollState,
    isAutoScrollingState,
} from '~/state/atoms/viewerAtoms';

/**
 * AutoScrolling Hook
 *
 * Method to scroll over all the slices within the bounding box of a given structure.
 * It starts from the first slice of the structure and slowly scrolls to the end slice and back when called.
 *
 * @param structure - The ROI number of the structure to scroll through.
 */
export const useAutoScroll = (structureNumber: number) => {
    const [shouldScroll, setShouldScroll] =
        useRecoilState(isAutoScrollingState);
    const [currentPosition, setCurrentPosition] = useRecoilState(
        currenPositionAutoScrollState
    );
    const isReviewToolOpen = useRecoilValue(reviewToolState);
    const scrollInterval = useRef<NodeJS.Timeout | null>(null);
    const environment = useRecoilValue(environmentState);
    const mainView = useRecoilValue(mainViewState);
    const upperSideView = useRecoilValue(upperSideViewState);
    const lowerSideView = useRecoilValue(lowerSideViewState);

    useEffect(() => {
        if (
            !isReviewToolOpen ||
            !shouldScroll ||
            !environment ||
            !mainView ||
            !upperSideView ||
            !lowerSideView
        ) {
            return;
        }

        const roi = environment.structureSet?.getRoi(structureNumber);
        if (!roi || !roi.texture) {
            return;
        }

        // Focus on the structure before scrolling
        environment.panZoom.focusTo(
            mainView,
            upperSideView,
            lowerSideView,
            roi.boundingBox
        );

        let scrollDirection = 1; // bouncing scroll direction

        const viewDirection = mainView.viewDirection;
        const { min, max, center } = roi.texture.contentBoundingBox;
        const imageGrid = roi._imageGrid;
        const start = vec3.add(
            vec3.create(),
            center,
            vec3.scale(
                vec3.create(),
                viewDirection,
                vec3.dot(
                    viewDirection,
                    vec3.subtract(vec3.create(), min!, center)
                )
            )
        );
        const end = vec3.add(
            vec3.create(),
            center,
            vec3.scale(
                vec3.create(),
                viewDirection,
                vec3.dot(
                    viewDirection,
                    vec3.subtract(vec3.create(), max!, center)
                )
            )
        );

        setCurrentPosition(vec3.clone(start));

        const step = vec3.dot(viewDirection, imageGrid.getRes()); // step through image slices
        const delay = 140; // Adjust for scroll interval

        // helper variables for direction change calculation
        const scrollLine = vec3.sub(vec3.create(), end, start);
        const scrollLineDot = vec3.dot(scrollLine, scrollLine);

        const scroll = () => {
            setCurrentPosition((prevPosition) => {
                if (!prevPosition) return null;

                const newPosition = vec3.add(
                    vec3.create(),
                    prevPosition,
                    vec3.scale(
                        vec3.create(),
                        viewDirection,
                        step * scrollDirection
                    )
                );

                const scrollDistance = vec3.dot(
                    vec3.sub(vec3.create(), newPosition, start),
                    scrollLine
                );

                if (scrollDistance < 0 || scrollDistance > scrollLineDot) {
                    scrollDirection *= -1;
                }

                return newPosition;
            });
            if (currentPosition) {
                mainView.updatePlaneIntersectionPoint(currentPosition);
            }

            // Schedule the next scroll step
            scrollInterval.current = setTimeout(scroll, delay);
        };
        scroll();

        return () => {
            if (scrollInterval.current) {
                clearTimeout(scrollInterval.current);
                scrollInterval.current = null;
            }
        };
    }, [
        isReviewToolOpen,
        shouldScroll,
        structureNumber,
        environment,
        mainView,
    ]);

    return { shouldScroll, setShouldScroll };
};
